import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, makeStyles } from '@material-ui/core';
import {
  Button,
  Checkbox,
  Flex,
  List,
  ListItem,
  Typography,
} from '@knockrentals/knock-shared-web-v2';

const DEFAULT_VISIBLE_COUNT = 5;

const getDistanceText = (distance) => {
  if (distance != null) {
    return `${Math.round(distance)} miles away`;
  }
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: 600,
  },
  divider: {
    '&.MuiDivider-root': {
      borderTop: 'unset',
      marginTop: 'unset',
    },
  },
  propertyItem: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '8px',
    margin: '8px 0',
  },
}));

const PropertyList = ({
  handleLinkedPropertyOnChange,
  selectedPropertyIds,
  teamProperties,
}) => {
  const [isShowingAllProperties, setIsShowingAllProperties] = useState(false);
  const classes = useStyles();

  const toggleIsShowingAllProperties = () =>
    setIsShowingAllProperties((prev) => !prev);

  if (!teamProperties || teamProperties.length === 0) {
    return (
      <Typography color="secondary.500" variant="body2">
        No additional phases available for this leasing team.
      </Typography>
    );
  }

  const visibleProperties = isShowingAllProperties
    ? teamProperties
    : teamProperties.slice(0, DEFAULT_VISIBLE_COUNT);
  const selectedCount = selectedPropertyIds.length;

  const selectedPropertiesText = `${selectedCount} ${selectedCount === 1 ? 'property' : 'properties'} selected`;
  const showingText = `Showing ${visibleProperties.length} closest properties`;
  const showAllButtonText = isShowingAllProperties
    ? 'Show less'
    : 'View all properties';

  return (
    <div className={classes.container}>
      <ListItem
        component="div"
        primary={
          <Flex justifyContent="space-between">
            <Typography variant="subtitle2">Select Properties</Typography>
            <Typography variant="captionStrong" color="textSecondary">
              {selectedPropertiesText}
            </Typography>
          </Flex>
        }
        secondary={showingText}
      />

      <Divider className={classes.divider} />

      <List disablePadding>
        {visibleProperties.map((property) => (
          <ListItem
            className={classes.propertyItem}
            dense
            key={property.id}
            primary={property.name}
            secondary={getDistanceText(property.distance)}
            startSlot={
              <Checkbox
                checked={selectedPropertyIds.includes(property.id)}
                color="default"
                inputProps={{ 'aria-label': property.name }}
                onChange={handleLinkedPropertyOnChange(property.id)}
              />
            }
          />
        ))}
      </List>

      {teamProperties.length > DEFAULT_VISIBLE_COUNT && (
        <Button
          color="primary"
          onClick={toggleIsShowingAllProperties}
          size="small"
          variant="text"
        >
          {showAllButtonText}
        </Button>
      )}
    </div>
  );
};

PropertyList.propTypes = {
  handleLinkedPropertyOnChange: PropTypes.func.isRequired,
  selectedPropertyIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  teamProperties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      distance: PropTypes.number,
    }),
  ),
};

export default PropertyList;
